import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BoyIcon from '@mui/icons-material/Boy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';
import PersonAvatar from '../../events/shared/PersonAvatar';

/**
 * SOIEventRow - Renders a Subject of Interest (SOI) event
 * SOI events occur when an unknown face is flagged by an operator as a person of interest
 */
const SOIEventRow = React.memo(function SOIEventRow({
  event,
  isChild = false,
  address,
  onLocationClick
}) {
  const hasValidMetadata = event.metadata &&
    typeof event.metadata === 'object' &&
    !event.metadata.S &&
    !event.metadata.M &&
    !event.metadata.NULL;

  if (!hasValidMetadata) {
    return null;
  }

  const capturedImageUrl = getCapturedImageDisplayUrl(event.metadata.capturedImageUrl);
  const croppedFaceUrl = getCapturedImageDisplayUrl(event.metadata.croppedFaceUrl);
  const timestamp = new Date(event.timestamp || event.createdAt);
  const unknownFaceId = event.metadata.unknownFaceId || 'Unknown';
  const sightingCount = event.metadata.sightingCount || 0;
  const promotionTrigger = event.metadata.promotionTrigger || 'UNKNOWN';

  // Determine background color
  const backgroundColor = isChild ? '#f5f5f5' : '#fff';
  const borderColor = isChild ? '4px solid #ff9800' : 'none';

  return (
    <TableRow
      hover
      sx={{
        backgroundColor,
        borderLeft: borderColor,
        '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
        '&:hover': {
          backgroundColor,
          filter: 'brightness(0.98)'
        }
      }}
    >
      {/* Event Type Icon */}
      <TableCell align="center" sx={{ width: 40 }}>
        <Tooltip title="Subject of Interest">
          <NotificationsActiveIcon sx={{ color: 'warning.main', fontSize: 28 }} />
        </Tooltip>
      </TableCell>

      {/* Expand/Collapse Column (empty for SOI events) */}
      <TableCell align="center" sx={{ width: 40 }} />

      {/* Event Details */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Promotion trigger indicator (Manual or Auto) */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
            flexShrink: 0
          }}>
            {/* Manual or Auto icon */}
            {promotionTrigger === 'MANUAL' ? (
              <BoyIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            ) : (
              <AutoAwesomeIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            )}

            {/* Indicator circle */}
            <Box sx={{
              position: 'relative',
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'conic-gradient(#ed6c02 360deg, rgba(0, 0, 0, 0.1) 0deg)'
            }}>
              <Box sx={{
                width: 23,
                height: 23,
                borderRadius: '50%',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    color: '#ed6c02',
                    lineHeight: 1
                  }}
                >
                  {promotionTrigger === 'MANUAL' ? 'M' : 'A'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* SOI face thumbnail - prefer cropped face over full capture */}
          {(croppedFaceUrl || capturedImageUrl) && (
            <Box sx={{
              border: '3px solid',
              borderColor: 'warning.main',
              borderRadius: '50%',
              padding: '2px',
              display: 'inline-flex',
              flexShrink: 0,
              '& .MuiAvatar-root': { mr: 0 }
            }}>
              <PersonAvatar
                imageUrl={croppedFaceUrl || capturedImageUrl}
                name="Subject of Interest"
                size={60}
              />
            </Box>
          )}

          {/* Event info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {event.description || 'Subject of Interest flagged'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {unknownFaceId}
              </Typography>
            </Box>
          </Box>
        </Box>
      </TableCell>

      {/* Timestamp */}
      <TableCell sx={{ width: 140 }}>
        <Typography variant="caption" color="text.secondary">
          {timestamp.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {timestamp.toLocaleDateString('en-GB')}
        </Typography>
      </TableCell>

      {/* User Account */}
      <TableCell sx={{ width: 120 }}>
        <Typography variant="caption" color="text.secondary">
          {event.userHandle || event.userEmail || event.userId || '—'}
        </Typography>
      </TableCell>

      {/* Location - Only show for automatic promotions */}
      <TableCell sx={{ width: 80 }} align="center">
        {promotionTrigger !== 'MANUAL' && event.location && (
          <Tooltip title={address || 'Show location'}>
            <IconButton
              size="small"
              onClick={() => onLocationClick?.(event.location)}
              sx={{ color: 'primary.main' }}
            >
              <LocationOnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
});

export default SOIEventRow;
